import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { AuthState, User } from '@/types'
import { authService } from '@/services/auth'

console.log('🏪 Auth store module loaded')
console.log('🏪 authService imported:', !!authService)

export const useAuthStore = defineStore('auth', () => {
  console.log('🏪 useAuthStore factory called')
  
  const user = ref<User | null>(null)
  const isLoading = ref(true)

  const isAuthenticated = computed(() => !!user.value)
  const isPremium = computed(() => user.value?.isPremium ?? false)

  async function signIn(email: string, password: string) {
    console.log('🏪 AuthStore.signIn called')
    console.log('🏪 Email:', email)
    console.log('🏪 Password length:', password?.length || 0)
    console.log('🏪 authService object:', authService)
    console.log('🏪 authService.signIn function:', typeof authService.signIn)
    
    isLoading.value = true
    try {
      console.log('🏪 Calling authService.signIn...')
      await authService.signIn(email, password)
      
      // Immediately get the current user after sign in
      const currentUser = await authService.getCurrentUser()
      user.value = currentUser
      console.log('✅ AuthStore.signIn successful, user set:', currentUser?.email)
    } catch (error) {
      console.error('❌ AuthStore.signIn error:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function signUp(email: string, password: string, name?: string) {
    console.log('🏪 AuthStore.signUp called')
    console.log('🏪 Email:', email)
    console.log('🏪 Password length:', password?.length || 0)
    console.log('🏪 Name:', name)
    
    isLoading.value = true
    try {
      await authService.signUp(email, password, name)
      
      // Immediately get the current user after sign up
      const currentUser = await authService.getCurrentUser()
      user.value = currentUser
      console.log('✅ AuthStore.signUp successful, user set:', currentUser?.email)
    } catch (error) {
      console.error('❌ AuthStore.signUp error:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  async function signOut() {
    isLoading.value = true
    try {
      await authService.signOut()
      user.value = null
    } finally {
      isLoading.value = false
    }
  }

  async function signInWithGoogle() {
    console.log('🏪 AuthStore.signInWithGoogle called')
    isLoading.value = true
    try {
      await authService.signInWithGoogle()
      // User will be set via onAuthStateChange callback after redirect
      console.log('✅ Google sign in initiated')
    } catch (error) {
      console.error('❌ AuthStore.signInWithGoogle error:', error)
      isLoading.value = false
      throw error
    }
  }

  async function signInWithFacebook() {
    console.log('🏪 AuthStore.signInWithFacebook called')
    isLoading.value = true
    try {
      await authService.signInWithFacebook()
      // User will be set via onAuthStateChange callback after redirect
      console.log('✅ Facebook sign in initiated')
    } catch (error) {
      console.error('❌ AuthStore.signInWithFacebook error:', error)
      isLoading.value = false
      throw error
    }
  }

  async function updateProfile(updates: Partial<Pick<User, 'name' | 'avatar'>>) {
    if (!user.value) return
    
    await authService.updateProfile(updates)
    user.value = { ...user.value, ...updates }
  }

  function setUser(newUser: User | null) {
    console.log('🏪 setUser called:', newUser?.email || 'null')
    user.value = newUser
    isLoading.value = false
  }

  async function initialize() {
    console.log('🏪 Auth store initialize called')
    
    // Check for existing session first
    const { data: { session } } = await authService.getCurrentSession()
    console.log('🏪 Existing session:', session ? 'found' : 'none')
    
    if (session?.user) {
      console.log('🏪 Restoring user from session:', session.user.email)
      const currentUser = await authService.getCurrentUser()
      user.value = currentUser
      isLoading.value = false
      console.log('🏪 User restored:', currentUser?.email)
    } else {
      isLoading.value = false
      console.log('🏪 No session to restore')
    }
    
    // Set up listener for future changes
    authService.onAuthStateChange((state: AuthState) => {
      console.log('🏪 Auth state change received:', state.user?.email || 'null')
      user.value = state.user
      isLoading.value = state.isLoading
    })
    
    console.log('🏪 Auth initialization complete')
  }

  return {
    user,
    isLoading,
    isAuthenticated,
    isPremium,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithFacebook,
    updateProfile,
    setUser,
    initialize
  }
})